import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  collection,
  query,
  where,
  getDocs,
  serverTimestamp,
  increment,
} from 'firebase/firestore';
import { db, auth } from '@/config/firebase.config';
import { UserProfile, CreateUserProfileData, UpdateUserProfileData } from '@/types';
import { COLLECTIONS } from '@/constants';
import { convertFirestoreDoc, calculateCommunityStatus } from '@/utils';

/**
 * Create a new user profile in Firestore
 */
export const createUserProfile = async (data: CreateUserProfileData): Promise<void> => {
  const user = auth.currentUser;
  if (!user) throw new Error('No authenticated user');

  const userRef = doc(db, COLLECTIONS.USERS, user.uid);

  const profileData: Omit<UserProfile, 'uid'> = {
    email: data.email,
    displayName: data.displayName || '',
    photoURL: '',
    bio: '',
    phoneNumber: '',
    postalCode: data.postalCode || '',
    language: data.language || 'en',
    role: 'user',
    communityStatus: 'new_neighbor',
    totalExchanges: 0,
    totalRatings: 0,
    averageRating: 0,
    servicesOffered: [],
    createdAt: new Date(),
    updatedAt: new Date(),
  };

  await setDoc(userRef, {
    ...profileData,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
};

/**
 * Get a user profile by user ID
 */
export const getUserProfile = async (userId: string): Promise<UserProfile | null> => {
  try {
    const userRef = doc(db, COLLECTIONS.USERS, userId);
    const userSnap = await getDoc(userRef);

    if (!userSnap.exists()) {
      return null;
    }

    return convertFirestoreDoc<UserProfile>({
      uid: userSnap.id,
      ...userSnap.data(),
    });
  } catch (error) {
    console.error('Error getting user profile:', error);
    throw error;
  }
};

/**
 * Update user profile
 */
export const updateUserProfile = async (
  userId: string,
  data: UpdateUserProfileData
): Promise<void> => {
  try {
    const userRef = doc(db, COLLECTIONS.USERS, userId);

    await updateDoc(userRef, {
      ...data,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error updating user profile:', error);
    throw error;
  }
};

/**
 * Get all users (admin only)
 */
export const getAllUsers = async (): Promise<UserProfile[]> => {
  try {
    const usersRef = collection(db, COLLECTIONS.USERS);
    const querySnapshot = await getDocs(usersRef);

    return querySnapshot.docs.map((doc) =>
      convertFirestoreDoc<UserProfile>({
        uid: doc.id,
        ...doc.data(),
      })
    );
  } catch (error) {
    console.error('Error getting all users:', error);
    throw error;
  }
};

/**
 * Increment user's total exchanges and update community status
 */
export const incrementUserExchanges = async (userId: string): Promise<void> => {
  try {
    const userRef = doc(db, COLLECTIONS.USERS, userId);
    const userSnap = await getDoc(userRef);

    if (!userSnap.exists()) {
      throw new Error('User not found');
    }

    const currentExchanges = userSnap.data()?.totalExchanges || 0;
    const newExchanges = currentExchanges + 1;
    const newStatus = calculateCommunityStatus(newExchanges);

    await updateDoc(userRef, {
      totalExchanges: increment(1),
      communityStatus: newStatus,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error incrementing user exchanges:', error);
    throw error;
  }
};

/**
 * Update user's rating after receiving a new rating
 */
export const updateUserRating = async (userId: string, newRating: number): Promise<void> => {
  try {
    const userRef = doc(db, COLLECTIONS.USERS, userId);
    const userSnap = await getDoc(userRef);

    if (!userSnap.exists()) {
      throw new Error('User not found');
    }

    const userData = userSnap.data();
    const currentTotal = userData.totalRatings || 0;
    const currentAverage = userData.averageRating || 0;

    const newTotal = currentTotal + 1;
    const newAverage = ((currentAverage * currentTotal) + newRating) / newTotal;

    await updateDoc(userRef, {
      totalRatings: newTotal,
      averageRating: newAverage,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error updating user rating:', error);
    throw error;
  }
};

/**
 * Check if user has admin role
 */
export const isAdmin = async (userId: string): Promise<boolean> => {
  try {
    const profile = await getUserProfile(userId);
    return profile?.role === 'admin';
  } catch (error) {
    console.error('Error checking admin status:', error);
    return false;
  }
};
